import React, { CSSProperties, ReactNode } from 'react';
import { Drawer } from 'antd';
import './style.css';

/**
 * Props for the CustomDrawer component.
 */
interface CustomDrawerProps {
  /** Function to be called when the drawer is closed. */
  onClose: () => void;
  /** Placement of the drawer ('top', 'right', 'bottom', 'left'). Defaults to 'right'. */
  placement?: 'top' | 'right' | 'bottom' | 'left';
  /** Whether to show the mask (overlay background). Defaults to true. */
  mask?: boolean;
  /** Custom close icon for the drawer. */
  closeIcon?: ReactNode;
  /** Content inside the drawer. */
  children?: ReactNode;
  /** Whether the drawer is open or closed. */
  open: boolean;
  /** Size of the drawer ('default' or 'large'). Defaults to 'default'. */
  size?: 'default' | 'large';
  /** Height of the drawer (used when placement is 'top' or 'bottom'). */
  height?: string | number;
  /** Custom styles for the drawer container. */
  style?: CSSProperties;
  /** Custom styles for the drawer body content. */
  bodyStyle?: CSSProperties;
  /** Width of the drawer (used when placement is 'left' or 'right'). */
  width?: string | number;
  /** Custom styles for the drawer header. */
  headerStyle?: CSSProperties;
}

/**
 * A customizable drawer component using Ant Design's Drawer.
 *
 * @param {CustomDrawerProps} props - The component props.
 * @returns {JSX.Element} The rendered CustomDrawer component.
 */
const CustomDrawer: React.FC<CustomDrawerProps> = ({
  onClose,
  placement = 'right',
  mask = true,
  closeIcon,
  children,
  open,
  size = 'default',
  height,
  style,
  bodyStyle,
  width,
  headerStyle,
}) => {
  return (
    <Drawer
      onClose={onClose}
      placement={placement}
      mask={mask}
      closeIcon={closeIcon}
      open={open}
      size={size}
      height={height}
      style={style}
      styles={{
        body: bodyStyle,
        header: headerStyle,
      }}
      width={width}
      id="customDrawer"
    >
      {children}
    </Drawer>
  );
};

export default CustomDrawer;
